import { Stack } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { useMemo } from 'react';
import {
  IMPORT_MORE,
  useDtoolsImport,
} from 'src/components/ImportDtoolsProjectModal/dtools-import-context';
import { ImportResultsList } from 'src/components/ImportProposalsModal/import-results-list';
import { formatProjectCount } from 'src/components/ImportProposalsModal/import-results.utils';
import { ImportSummary } from 'src/components/ImportProposalsModal/import-summary';
import { ModalStep, useModalStep } from 'src/components/ModalContainer';
import { Modals, useModalMethods } from 'src/store/modals';
import { useNavigateRoute } from 'src/utils/routing';

/** Step 3: per-project results of the import; it opens while the job still runs and fills in. */
export const ImportJobDialog = () => {
  const { popStep } = useModalStep();
  const { previewProjects, importOutcome, close } = useDtoolsImport();
  const { closeModal } = useModalMethods(Modals.ImportDtoolsProjects);
  const { navigate } = useNavigateRoute();
  const { isFinished, projectIds, records, errors, loading, hasMoreResults, loadMoreResults } =
    importOutcome;
  const projectNames = useMemo(
    () => new Map(previewProjects.map((p) => [p.id.toString(), p.name])),
    [previewProjects],
  );

  // Every project failing is still a failed import, even though the job itself finished.
  const importFailed = isFinished && projectIds.length > 0 && records.length === 0;
  const title = (() => {
    if (!isFinished) {
      return 'Importing';
    }
    return importFailed ? 'Import failed' : 'Import complete';
  })();
  const isLoading = loading || !isFinished;

  return (
    <ModalStep
      title={title}
      actions={[
        {
          label: 'Import more',
          kind: 'secondary',
          onClick: () => popStep(IMPORT_MORE),
          disabled: !isFinished,
          testId: 'import-dtools-projects-results-import-more',
        },
        {
          label: isFinished ? 'Done' : 'Close',
          kind: 'primary',
          onClick: close,
          testId: 'import-dtools-projects-results-done',
        },
      ]}
    >
      <Stack gap={1.5} data-testid="import-dtools-projects-results">
        <ImportSummary
          status={
            isLoading
              ? `Importing ${formatProjectCount(previewProjects.length)}…`
              : `${records.length} of ${formatProjectCount(projectIds.length)} imported`
          }
          records={isLoading ? undefined : records}
          tense="done"
          testId="import-dtools-projects-result"
        />
        <ImportResultsList
          records={records}
          errors={errors}
          projectNames={projectNames}
          loading={isLoading}
          loadMore={hasMoreResults ? { onSelect: loadMoreResults } : undefined}
          onOpenSite={(addressId) => {
            closeModal();
            navigate(portalPaths.site.full(addressId));
          }}
          testId="import-dtools-projects-result"
        />
      </Stack>
    </ModalStep>
  );
};
